import { ReactNode } from 'react';

import { CurrentPointOnboardingType } from 'application/components/common/SelectPlayerFlow/types';
import { ModalTypes, useModalContext } from 'application/providers/ModalProvider';
import { RouterUtils, useNavigate } from 'application/providers/RouterProvider';
import { useAccountCompanyId } from 'application/hooks';
import { INTEGRATION_PAGE_PATH } from 'application/consts';
import {
  startAcceptanceAdvancedSigning,
  openSigningPreloadTab,
  selectAcceptanceAdvancedContext,
} from 'application/store';
import { DEFAULT_ACCEPTANCE_ADVANCED_ID } from 'application/store/slices/acceptanceAdvancedSlice/consts';
import { useAppDispatch, useAppSelector } from 'application/providers/StoreProvider';

import { AcceptanceAdvanceStatus, PaymentKind, PaymentMethodObjectPureType } from '@shared/api/graphql/generated';
import { buildAbsoluteUrl } from '@shared/lib';

import { LicensesModalAction, LicensesModalState } from './licensesModalReducer';
import createLicensesModalBindCard from './createLicensesModalBindCard';
import createLicensesModalSubmitFlow from './createLicensesModalSubmitFlow';

const URI_FOR_RETURN_PAYMENT_INTEGRATION = buildAbsoluteUrl(
  RouterUtils.generatePath(INTEGRATION_PAGE_PATH, { type: 'sbbol' })
);

interface Props {
  state: LicensesModalState;
  dispatch: (value: LicensesModalAction) => void;
  close: (count?: number) => void;
  currentPoint: CurrentPointOnboardingType;
  setCurrentPoint: (currentPoint: CurrentPointOnboardingType) => void;
  closeModalButton: ReactNode;
  unpaidLicensesOnly: boolean;
  isNeedPayment: boolean;
  hasTrialLicenses: boolean;
  hasInactiveLicenses: boolean;
  hasWaitPaymentLicenses: boolean;
  hasGraceLicenses: boolean;
  paymentMethod: PaymentMethodObjectPureType | null;
  selectedPaymentKind: PaymentKind;
  acceptanceAdvanceStatus: AcceptanceAdvanceStatus | null;
  toEnableAutoRenewalPaid: string[];
  toEnableAutoRenewalUnpaid: string[];
  suspendAutoRenewal: (companyId: string, licensesIds: string[]) => Promise<unknown>;
  resumeAutoRenewal: (companyId: string, licensesIds: string[]) => Promise<unknown>;
  refetchBillingInfo: () => Promise<unknown>;
  getLinkForBindingCard: (uriForReturn: string, companyId: string) => Promise<string | undefined>;
  getLinkForPayment: (uriForReturn: string, companyId: string, licensesIds: string[]) => Promise<string | undefined>;
}

const useLicensesModalActions = ({
  state,
  dispatch,
  close,
  currentPoint,
  setCurrentPoint,
  closeModalButton,
  unpaidLicensesOnly,
  isNeedPayment,
  hasTrialLicenses,
  hasInactiveLicenses,
  hasWaitPaymentLicenses,
  hasGraceLicenses,
  paymentMethod,
  selectedPaymentKind,
  acceptanceAdvanceStatus,
  toEnableAutoRenewalPaid,
  toEnableAutoRenewalUnpaid,
  suspendAutoRenewal,
  resumeAutoRenewal,
  refetchBillingInfo,
  getLinkForBindingCard,
  getLinkForPayment,
}: Props) => {
  const companyId = useAccountCompanyId();
  const navigate = useNavigate();
  const storeDispatch = useAppDispatch();
  const { openModal } = useModalContext();
  const acceptanceAdvancedContext = useAppSelector(selectAcceptanceAdvancedContext(DEFAULT_ACCEPTANCE_ADVANCED_ID));

  const { handleBindCard } = createLicensesModalBindCard({
    companyId,
    state,
    getLinkForBindingCard,
    refetchBillingInfo,
  });

  const { handleChangeAutoRenewal } = createLicensesModalSubmitFlow({
    companyId,
    navigate,
    setCurrentPoint,
    currentPoint,
    close,
    openModal,
    closeModalButton,
    unpaidLicensesOnly,
    isNeedPayment,
    hasTrialLicenses,
    hasInactiveLicenses,
    hasWaitPaymentLicenses,
    hasGraceLicenses,
    paymentMethod,
    selectedPaymentKind,
    acceptanceAdvanceStatus,
    toSuspendAutorenewal: state.toSuspendAutorenewal,
    toEnableAutorenewal: state.toEnableAutorenewal,
    toEnableAutoRenewalPaid,
    toEnableAutoRenewalUnpaid,
    suspendAutoRenewal,
    resumeAutoRenewal,
    refetchBillingInfo,
    getLinkForPayment,
  });

  const setEditMode = () => {
    dispatch({ type: 'setIsViewMode', isViewMode: false });
  };

  const openAcceptanceAdvancedSigning = () => {
    const hasActiveTab = acceptanceAdvancedContext?.hasActiveTab;
    const preOpenedTab = hasActiveTab ? null : openSigningPreloadTab();

    storeDispatch(
      startAcceptanceAdvancedSigning({
        companyId,
        uriForReturn: URI_FOR_RETURN_PAYMENT_INTEGRATION,
        fallbackFn: () => {
          openModal(ModalTypes.SBERBUSSINESS_OPEN, {});
        },
        preOpenedTab,
      })
    );
  };

  return {
    handleBindCard,
    handleChangeAutoRenewal,
    setEditMode,
    openAcceptanceAdvancedSigning,
  };
};

export default useLicensesModalActions;
